import { useEffect, useState } from "react";
import { Eye, Users, Repeat, Clock } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Compact engagement panel for a share token (pitch or firm).
 * - Auto-polls every 25s while mounted.
 * - Shows total views, unique visitors, re-visits, last opened (relative),
 *   a 14-day sparkline, and up to 5 most-recent visitor rows.
 *
 * Props:
 *   token         : the opaque share token
 *   kind          : "pitch" | "firm"
 *   compact       : if true, render a single-line summary (used in client request rows)
 */
export default function ShareAnalytics({ token, kind = "pitch", compact = false }) {
    const [stats, setStats] = useState(null);
    const [error, setError] = useState("");

    const load = async () => {
        if (!token) return;
        try {
            const { data } = await api.get(`/share/analytics/${token}`, { params: { kind } });
            setStats(data);
            setError("");
        } catch (e) {
            setError(e?.response?.data?.detail || "Could not load analytics");
        }
    };

    useEffect(() => {
        load();
        const id = setInterval(() => {
            // Pause polling when tab is hidden to avoid wasted backend hits
            if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
            load();
        }, 25000);
        const onVis = () => { if (document.visibilityState === "visible") load(); };
        if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVis);
        return () => {
            clearInterval(id);
            if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVis);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, kind]);

    if (error) {
        return (
            <div className="font-mono text-[10px] text-zinc-600" data-testid={`share-analytics-error-${token}`}>
                {error}
            </div>
        );
    }

    if (!stats) {
        return (
            <div className="font-mono text-[10px] text-zinc-600" data-testid={`share-analytics-loading-${token}`}>
                Loading engagement…
            </div>
        );
    }

    const rel = relative(stats.last_viewed_at);
    const maxBar = Math.max(1, ...stats.series.map((s) => s.views));

    if (compact) {
        return (
            <div className="inline-flex items-center gap-3 font-mono text-[10px] text-zinc-400" data-testid={`share-analytics-compact-${token}`}>
                <span className="inline-flex items-center gap-1">
                    <Eye className="w-3 h-3 text-emerald-400" data-testid="sa-eye" />
                    <span data-testid={`sa-total-${token}`}>{stats.total_views}</span>
                    <span className="text-zinc-600">views</span>
                </span>
                <span className="inline-flex items-center gap-1">
                    <Users className="w-3 h-3 text-zinc-500" />
                    <span data-testid={`sa-uniques-${token}`}>{stats.unique_visitors}</span>
                    <span className="text-zinc-600">unique</span>
                </span>
                {stats.revisits > 0 && (
                    <span className="inline-flex items-center gap-1 text-amber-400">
                        <Repeat className="w-3 h-3" />
                        <span data-testid={`sa-revisits-${token}`}>{stats.revisits}</span>
                        <span className="text-amber-500/70">re-opens</span>
                    </span>
                )}
                {rel && (
                    <span className="inline-flex items-center gap-1 text-zinc-500">
                        <Clock className="w-3 h-3" />
                        <span data-testid={`sa-last-${token}`}>last {rel}</span>
                    </span>
                )}
            </div>
        );
    }

    return (
        <div className="border border-zinc-800 bg-zinc-950 px-4 py-3" data-testid={`share-analytics-${token}`}>
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-400">Engagement · live</div>
                <div className="font-mono text-[10px] text-zinc-600">Auto-refresh 25s</div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <Stat label="Views" value={stats.total_views} icon={<Eye className="w-3.5 h-3.5" />} tone="emerald" testid="sa-card-views" />
                <Stat label="Unique" value={stats.unique_visitors} icon={<Users className="w-3.5 h-3.5" />} tone="zinc" testid="sa-card-uniques" />
                <Stat label="Re-opens" value={stats.revisits} icon={<Repeat className="w-3.5 h-3.5" />} tone={stats.revisits > 0 ? "amber" : "zinc"} testid="sa-card-revisits" />
                <Stat label="Last opened" value={rel || "—"} icon={<Clock className="w-3.5 h-3.5" />} tone="zinc" small testid="sa-card-last" />
            </div>

            {/* Engagement depth — visitors who actively dragged the ROI calculator */}
            {(stats.slider_interactions > 0 || stats.engaged_visitors > 0) && (
                <div className="border border-emerald-500/30 bg-emerald-950/10 px-3 py-2 mb-4 flex items-center justify-between gap-3 flex-wrap" data-testid="sa-engagement-depth">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-300 flex items-center gap-1.5">
                        <Repeat className="w-3 h-3" /> Engagement depth
                    </div>
                    <div className="flex items-center gap-3 font-mono text-[10px] text-zinc-300">
                        <span data-testid="sa-slider-count"><span className="text-emerald-400 font-bold">{stats.slider_interactions}</span> slider drags</span>
                        <span className="text-zinc-700">·</span>
                        <span data-testid="sa-engaged-visitors"><span className="text-emerald-400 font-bold">{stats.engaged_visitors}</span> engaged visitor{stats.engaged_visitors === 1 ? "" : "s"}</span>
                    </div>
                </div>
            )}

            {/* 14-day sparkline */}
            <div className="mb-3" data-testid="sa-sparkline">
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-600 mb-1">Last 14 days</div>
                <div className="flex items-end gap-0.5 h-12">
                    {stats.series.map((s) => {
                        const h = Math.round((s.views / maxBar) * 100);
                        return (
                            <div key={s.date} className="flex-1 flex flex-col justify-end" title={`${s.date}: ${s.views} view${s.views === 1 ? "" : "s"}`}>
                                <div
                                    className={`w-full ${s.views > 0 ? "bg-emerald-500/70" : "bg-zinc-800"}`}
                                    style={{ height: `${Math.max(s.views > 0 ? 8 : 4, h)}%` }}
                                />
                            </div>
                        );
                    })}
                </div>
                <div className="flex justify-between font-mono text-[9px] text-zinc-700 mt-1">
                    <span>{stats.series[0]?.date.slice(5)}</span>
                    <span>today</span>
                </div>
            </div>

            {/* Recent visitors */}
            {stats.recent_visitors?.length > 0 && (
                <div data-testid="sa-recent-visitors">
                    <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-600 mb-1.5">Recent visitors (anonymized)</div>
                    <div className="space-y-1">
                        {stats.recent_visitors.slice(0, 5).map((v) => (
                            <div key={v.visitor} className="flex items-center justify-between gap-2 font-mono text-[10px] text-zinc-400 border-b border-zinc-900 py-1" data-testid={`sa-visitor-${v.visitor}`}>
                                <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-zinc-600">#{v.visitor}</span>
                                    <span className="text-zinc-500">{v.ua}</span>
                                    {v.count > 1 && (
                                        <span className="inline-flex items-center gap-0.5 text-amber-400">
                                            <Repeat className="w-2.5 h-2.5" /> {v.count}x
                                        </span>
                                    )}
                                    {v.interacted && (
                                        <span className="inline-flex items-center text-emerald-400 border border-emerald-500/40 bg-emerald-500/10 px-1 text-[9px] uppercase tracking-wider" title="This visitor dragged the ROI calculator">engaged</span>
                                    )}
                                </div>
                                <div className="text-zinc-600 shrink-0">{relative(v.last_ts)}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {stats.total_views === 0 && (
                <div className="font-mono text-[10px] text-zinc-600 mt-1">No views yet. Share the link to your prospect and we'll track opens here.</div>
            )}
        </div>
    );
}

function Stat({ label, value, icon, tone = "zinc", small = false, testid }) {
    const toneClass =
        tone === "emerald"
            ? "border-emerald-500/30 text-emerald-300"
            : tone === "amber"
                ? "border-amber-500/40 text-amber-300"
                : "border-zinc-800 text-zinc-300";
    return (
        <div className={`border ${toneClass} bg-black px-2.5 py-2`} data-testid={testid}>
            <div className="flex items-center gap-1 text-zinc-500 mb-0.5">
                {icon}
                <span className="font-mono text-[9px] uppercase tracking-[0.2em]">{label}</span>
            </div>
            <div className={`font-heading font-black ${small ? "text-sm" : "text-xl"}`}>{value}</div>
        </div>
    );
}

function relative(iso) {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    if (isNaN(t)) return "";
    const diff = (Date.now() - t) / 1000;
    if (diff < 60) return `${Math.max(1, Math.floor(diff))}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
}
